import React from 'react';
import ImageUpload from '../ui/ImageUpload';
import Select from '../ui/Select';
import Input from '../ui/Input';
import TokenChips from './TokenChips';
import ShortLinkField from './ShortLinkField';
import WhatsappPreview from '../campaigns/WhatsappPreview';
import { campaignService } from '../../services/campaignService';
import styles from './MessagingFields.module.css';

const FETCH_DEBOUNCE_MS = 500;

const LIST_PLACEHOLDERS = {
  loading: 'Loading templates…',
  error: "Couldn't load templates — check WhatsApp is configured",
  loaded_empty: 'No approved templates found',
};

// WhatsApp now sends via AWS End User Messaging Social (whatsapp.provider.js)
// — template-only, matching pickmywork-backend's own AWS WhatsApp sending,
// which never implemented free-form text either (unlike their separate
// Exotel path). No more "Send as" template-vs-free-text choice — every send
// is a pre-approved template.
class WhatsappFields extends React.Component {
  constructor(props) {
    super(props);
    this.state = { fetchedTemplate: null, fetchStatus: 'idle', templateList: [], templateListStatus: 'loading' };
    this._debounceTimer = null;
    // Guards against a slow earlier request resolving after a faster later
    // one and clobbering it with stale data — only the latest request's
    // result is ever applied.
    this._fetchSeq = 0;
  }

  componentDidMount() {
    // Fetches immediately on mount too (not just on change) so editing an
    // existing campaign shows the real template right away.
    this._scheduleFetch(this.props.templateName);
    this._loadTemplateList();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.templateName !== this.props.templateName) {
      this._scheduleFetch(this.props.templateName);
    }
  }

  componentWillUnmount() {
    clearTimeout(this._debounceTimer);
  }

  _loadTemplateList() {
    campaignService.listWhatsappTemplates()
      .then((result) => {
        this.setState({ templateList: result.templates, templateListStatus: result.reason ? 'error' : 'loaded' });
      })
      .catch(() => this.setState({ templateListStatus: 'error' }));
  }

  _scheduleFetch(templateName) {
    clearTimeout(this._debounceTimer);
    if (!templateName || !templateName.trim()) {
      this._fetchSeq++;
      this.setState({ fetchedTemplate: null, fetchStatus: 'idle' });
      return;
    }
    this.setState({ fetchStatus: 'loading' });
    this._debounceTimer = setTimeout(() => this._fetch(templateName), FETCH_DEBOUNCE_MS);
  }

  _fetch(templateName) {
    const seq = ++this._fetchSeq;
    campaignService.getWhatsappTemplate({ templateName })
      .then((result) => {
        if (seq !== this._fetchSeq) return;
        if (result.found) this.setState({ fetchedTemplate: result.template, fetchStatus: 'found' });
        else this.setState({ fetchedTemplate: null, fetchStatus: result.reason === 'not_configured' ? 'error' : 'not_found' });
      })
      .catch(() => {
        if (seq !== this._fetchSeq) return;
        this.setState({ fetchedTemplate: null, fetchStatus: 'error' });
      });
  }

  render() {
    const { templateName = '', templateParams = '', imageUrl = '', attributionLinkId = '', onChange, showPreview = true } = this.props;
    const { fetchedTemplate, fetchStatus, templateList, templateListStatus } = this.state;

    const options = templateList.map((t) => ({
      value: t.name,
      label: t.language && t.language !== 'en_US' ? `${t.name} (${t.language})` : t.name,
    }));
    const placeholder = templateListStatus === 'loaded' && !options.length
      ? LIST_PLACEHOLDERS.loaded_empty
      : LIST_PLACEHOLDERS[templateListStatus] || 'Select a template';

    // The approved template itself dictates this — a HEADER component with
    // format IMAGE means WhatsApp will reject the send without one.
    const imageRequired = fetchedTemplate?.components?.some((c) => c.type === 'HEADER' && c.format === 'IMAGE') || false;

    return (
      <div className={showPreview ? styles.msgLayout : undefined}>
        <div className={styles.msgFields}>
          <div className={styles.fi}>
            <label className={styles.fiLabel}>Template name <span className={styles.req}>*</span></label>
            <Select
              placeholder={placeholder}
              value={templateName}
              options={options}
              onChange={val => onChange('templateName', val)}
            />
            <div className={styles.fiHint}>Approved templates from your WhatsApp Business account.</div>
          </div>
          <div className={styles.fi}>
            <label className={styles.fiLabel}>Template params <span className={styles.opt}>(comma-separated, in order)</span></label>
            <Input placeholder="e.g. Rahul, 10%" value={templateParams} onChange={e => onChange('templateParams', e.target.value)} />
            <TokenChips onInsert={token => onChange('templateParams', templateParams ? `${templateParams}, ${token}` : token)} />
            <ShortLinkField
              attributionLinkId={attributionLinkId}
              onTag={link => onChange('attributionLinkId', link.id)}
              onUntag={() => onChange('attributionLinkId', '')}
            />
          </div>
          <div className={styles.fi}>
            <ImageUpload
              label={<>Header image {imageRequired ? <span className={styles.req}>*</span> : <span className={styles.opt}>(optional)</span>}</>}
              value={imageUrl}
              onChange={url => onChange('imageUrl', url)}
            />
            {imageRequired && !imageUrl && (
              <div className={styles.fiHint} style={{ color: 'var(--redHi)' }}>
                This template's approved header is an image — required before sending.
              </div>
            )}
          </div>
        </div>
        {showPreview && (
          <WhatsappPreview
            templateName={templateName}
            templateParams={templateParams}
            imageUrl={imageUrl}
            fetchedTemplate={fetchedTemplate}
            fetchStatus={fetchStatus}
          />
        )}
      </div>
    );
  }
}

export default WhatsappFields;
